/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiField} from "./UiField";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiNumberFieldConfig, UiNumberFieldSubCommandHandler, UiNumberFieldSubEventSource} from "../../generated/UiNumberFieldConfig";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {Constants, formatNumber} from "../Common";
import {keyCodes} from "trivial-components";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiTextInputHandlingField_SpecialKeyPressedSubEvent, UiTextInputHandlingField_TextInputSubEvent} from "../../generated/UiTextInputHandlingFieldConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {SubEventFactory} from "../../generated/SubEventFactory";
import {UiSpecialKey} from "../../generated/UiSpecialKey";
import {UiNumberFieldSliderMode} from "../../generated/UiNumberFieldSliderMode";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
export class UiNumberField extends UiField<UiNumberFieldConfig, number> implements UiNumberFieldSubEventSource, UiNumberFieldSubCommandHandler {

	public readonly onTextInput: TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_TextInputSubEvent>(this, 250);
	public readonly onSpecialKeyPressed: TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent> = new TeamAppsEvent<UiTextInputHandlingField_SpecialKeyPressedSubEvent>(this, 250);

	private $wrapper: JQuery;
	private $clearableFieldWrapper: JQuery;
	private $field: JQuery;
	private precision: number;
	private showClearButton: boolean;
	private $slider: JQuery;
	private $sliderHandle: JQuery;

	private minValue: number;
	private maxValue: number;
	private sliderMode: UiNumberFieldSliderMode;
	private sliderStep: number;
	private commitOnSliderChange: boolean;

	protected initialize(config: UiNumberFieldConfig, targetElement: Element, context: TeamAppsUiContext) {
		this.$wrapper = $(`<div class="UiNumberField form-control">
	<div class="clearable-field-wrapper">
		<input autocomplete="off" type="text"/>
		<div class="clear-button tr-remove-button"/> 
	</div>             
    <div class="slider">
		<div class="slider-track"></div>
		<div class="slider-handle"></div>			
	</div>         
</div>`);
		this.$clearableFieldWrapper = this.$wrapper.find(".clearable-field-wrapper");
		this.$field = this.$wrapper.find("input");
		this.$slider = this.$wrapper.find(".slider");
		const $sliderTrack = this.$wrapper.find(".slider-track");
		this.$sliderHandle = this.$wrapper.find(".slider-handle");
		let $clearButton = this.$wrapper.find('.clear-button');
		$clearButton.click(() => {
			this.$field.val("");
			this.commit();
			this.updateClearButton();
			this.setSliderPositionByValue(this.getTransientValue());
		});

		this.setEditingMode(config.editingMode);
		this.setEmptyText(config.emptyText);
		this.setPrecision(config.precision);
		this.setShowClearButton(config.showClearButton);
		this.setCommitOnSliderChange(config.commitOnSliderChange);

		this.$field.keyup(() => {
			this.setSliderPositionByValue(this.getTransientValue());
		});

		this.$field.focus(() => {
			if (this.getEditingMode() !== UiFieldEditingMode.READONLY) {
				this.$wrapper.addClass("focus");
				this.$field.select();
			}
		});
		this.$field.blur((e) => {
			if (this.getEditingMode() !== UiFieldEditingMode.READONLY) {
				this.commit();
				this.updateClearButton();
				this.$wrapper.removeClass("focus");
			}
		});
		this.$field.on("input", () => {
			this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(this.$field.val().toString()));
			this.updateClearButton();
		});
		this.$field.keydown((e) => {
			if (e.keyCode === keyCodes.escape) {
				this.displayCommittedValue(); // back to committedValue
				this.onTextInput.fire(SubEventFactory.createUiTextInputHandlingField_TextInputSubEvent(this.$field.val().toString()));
				this.$field.select();
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ESCAPE));
			} else if (e.keyCode === keyCodes.enter) {
				this.commit();
				this.onSpecialKeyPressed.fire(SubEventFactory.createUiTextInputHandlingField_SpecialKeyPressedSubEvent(UiSpecialKey.ENTER));
			}
		});

		this.$wrapper.click((e) => {
			if (e.target !== this.$field[0]) {
				this.focus();
			}
		});
		this.$wrapper.appendTo(targetElement);

		this.$sliderHandle.on(`${Constants.POINTER_EVENTS.start}`, (e) => {
			const isSpecialMouseButton = e.button != null && e.button !== 0;
			if (isSpecialMouseButton || this.getEditingMode() === UiFieldEditingMode.DISABLED) {
				return;
			}
			if (!this.hasFocus()) {
				this.focus();
			}
			e.preventDefault(); // do not lose the focus!
			let pointerElementOffsetX = e.offsetX;
			let moveHandler = (e: any) => {
				let sliderClientX = this.$slider[0].getBoundingClientRect().left;
				let aPrioriNewSliderPositionX = e.clientX - pointerElementOffsetX - sliderClientX;
				let [minSliderX, maxSliderX] = this.getSliderMinMaxPositionX();
				const aPrioriNewValue = this.minValue + ((aPrioriNewSliderPositionX / (maxSliderX - minSliderX)) * (this.maxValue - this.minValue));
				let newValue = this.coerceToSteppedValue(aPrioriNewValue);
				this.setSliderPositionByValue(newValue);
				this.$field.val("" + newValue);
				this.$field.select();
				this.ensureDecimalInput();
				this.updateClearButton();
			};
			$(document).on(`${Constants.POINTER_EVENTS.move}`, moveHandler);
			let endHandler = () => {
				$(document).off(`${Constants.POINTER_EVENTS.move}`, moveHandler);
				$(document).off(`${Constants.POINTER_EVENTS.end}`, endHandler);
				this.focus();
				if (this.commitOnSliderChange) {
					this.commit();
				}
			};
			$(document).on(`${Constants.POINTER_EVENTS.end}`, endHandler);
		});

		this.setMinValue(config.minValue);
		this.setMaxValue(config.maxValue);
		this.setSliderMode(config.sliderMode);
		this.setSliderStep(config.sliderStep);
	}

	commit(forceEvenIfNotChanged?: boolean): boolean {
		let boundedValue = this.getBoundedValue(this.getTransientValue());
		this.setEditorValue(boundedValue);
		return super.commit(forceEvenIfNotChanged);
	}

	private setEditorValue(value: number) {
		this.$field.val(this.formatNumber(value));
	}

	private setSliderPositionByValue(newValue: number) {
		if (newValue == null) {
			newValue = (this.minValue + this.maxValue) / 2; // display slider at middle of track
		}
		let [minSliderX, maxSliderX] = this.getSliderMinMaxPositionX();
		let newHandleX = minSliderX + (maxSliderX - minSliderX) * ((newValue - this.minValue) / (this.maxValue - this.minValue));
		newHandleX = Math.max(minSliderX, Math.min(newHandleX, maxSliderX));
		this.$sliderHandle.css("left", `${newHandleX}px`);
	}

	private getSliderMinMaxPositionX() {
		return [1, this.$slider[0].offsetWidth - 1 - this.$sliderHandle[0].offsetWidth];
	}

	private coerceToSteppedValue(aPrioriNewValue: number) {
		const boundedNewValue = this.getBoundedValue(aPrioriNewValue);
		let totalNumberOfTicks = ((this.maxValue - this.minValue) / this.sliderStep);
		let tickIndex = Math.round(((boundedNewValue - this.minValue) / (this.maxValue - this.minValue)) * totalNumberOfTicks);
		let newValue = Math.min(this.maxValue, this.minValue + tickIndex * this.sliderStep);
		if (newValue > this.maxValue) {
			newValue -= this.sliderStep;
		}
		return newValue;
	}

	private getBoundedValue(unboundedValue: number) {
		if (unboundedValue == null) {
			return unboundedValue;
		}
		return Math.max(this.minValue, Math.min(unboundedValue, this.maxValue));
	}

	onResize(): void {
		this.setSliderPositionByValue(this.getTransientValue());
	}

	private ensureDecimalInput() {
		const cursorPosition = (<HTMLInputElement>this.$field[0]).selectionEnd;
		const oldValue = this.$field.val().toString();

		let newValue = this.convertInputToDecimalValue(oldValue);

		if (oldValue !== this.formatNumber(newValue)) {
			this.setEditorValue(newValue);

			const newCursorPosition = Math.min(this.$field.val().toString().length, cursorPosition);

			try {
				(<HTMLInputElement>this.$field[0]).setSelectionRange(newCursorPosition, newCursorPosition);
			} catch (e) {
				// IE throws an error when invoking setSelectionRange before the element is rendered...
			}
		}
	}

	isValidData(v: number): boolean {
		return v == null || typeof v === "number";
	}

	private convertInputToDecimalValue(oldValue: string): number {
		let decimalSeparator = this._context.config.decimalSeparator;
		let newValue = oldValue.replace(new RegExp('[^\-0-9' + decimalSeparator + ']', 'g'), '');
		newValue = newValue.replace(/(\d*\.\d*)\./g, '$1'); // only one decimal separator!!
		newValue = newValue.replace(/(.)-*/g, '$1'); // minus may only occure at the beginning

		const decimalSeparatorIndex = newValue.indexOf(decimalSeparator);
		if (this.precision >= 0 && decimalSeparatorIndex != -1 && newValue.length - decimalSeparatorIndex - 1 > this.precision) {
			newValue = newValue.substring(0, decimalSeparatorIndex + 1 + this.precision);
		}
		newValue = newValue.replace(decimalSeparator, '.');
		return parseFloat(newValue);
	}

	public getMainDomElement(): JQuery {
		return this.$wrapper;
	}

	public getFocusableElement(): JQuery {
		return this.$field;
	}

	protected displayCommittedValue(): void {
		let value = this.getCommittedValue();
		this.$field.val(value != null ? this.formatNumber(value) : "");
		this.updateClearButton();
		this.setSliderPositionByValue(value);
	}

	private formatNumber(value: number) {
		return formatNumber(value, this.precision, this._context.config.decimalSeparator, "");
	}

	public getTransientValue(): number {
		if (this.$field.val() === "") {
			return null;
		} else {
			let value = this.convertInputToDecimalValue(this.$field.val().toString());
			if (value > this.maxValue) {
				value = this.maxValue;
			} else if (value < this.minValue) {
				value = this.minValue;
			}
			return value;
		}
	}

	focus(): void {
		this.$field.select();
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		UiField.defaultOnEditingModeChangedImpl(this);
	}

	destroy(): void {
		this.$field.remove();
	}

	public static getReadOnlyHtml(config: UiNumberFieldConfig, value: number, context: TeamAppsUiContext, drawBorder: boolean): string {
		let formatedValue: string;
		if (value != null && value != null) {
			formatedValue = formatNumber(value, config.precision, context.config.decimalSeparator, context.config.thousandsSeparator);
		} else {
			formatedValue = "";
		}
		return `<div class="static-readonly-UiNumberField ${drawBorder ? "bordered" : ""}">${formatedValue}</div>`;
	}

	getDefaultValue(): number {
		return null; // yes
	}

	setPrecision(precision: number): void {
		this.precision = precision;
		this.ensureDecimalInput();
	}

	setEmptyText(emptyText: string): void {
		this.$field.attr('placeholder', emptyText || '');
	}

	setShowClearButton(showClearButton: boolean): void {
		this.showClearButton = showClearButton;
		this.updateClearButton();
	}

	private updateClearButton() {
		this.$clearableFieldWrapper.toggleClass("clearable", !!(this.showClearButton && this.getTransientValue()));
	}

	public valuesChanged(v1: number, v2: number): boolean {
		return v1 !== v2;
	}

	setMinValue(minValue: number): void {
		this.minValue = minValue;
		this.setSliderPositionByValue(this.getTransientValue());
	}

	setMaxValue(maxValue: number): void {
		this.maxValue = maxValue;
		this.setSliderPositionByValue(this.getTransientValue());
	}

	setSliderMode(sliderMode: UiNumberFieldSliderMode): void {
		this.sliderMode = sliderMode;
		this.$wrapper.toggleClass("slider-mode-disabled", sliderMode === UiNumberFieldSliderMode.DISABLED);
		this.$wrapper.toggleClass("slider-mode-visible", sliderMode === UiNumberFieldSliderMode.VISIBLE);
		this.$wrapper.toggleClass("slider-mode-visible-if-focused", sliderMode === UiNumberFieldSliderMode.VISIBLE_IF_FOCUSED);
	}

	setSliderStep(sliderStep: number): void {
		this.sliderStep = sliderStep;
	}

	setCommitOnSliderChange(commitOnSliderChange: boolean): void {
		this.commitOnSliderChange = commitOnSliderChange;
	}
}

TeamAppsUiComponentRegistry.registerFieldClass("UiNumberField", UiNumberField);